"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect } from 'react';
import { useServiceWorker } from '@/hooks/useServiceWorker';
import { WifiOff, Wifi, Download, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import UpdateNotification from './UpdateNotification';
export default function PWAStatus() {
  const $ = _c(30);
  const {
    isOnline,
    isInstallable,
    installPWA,
    dismissInstallPrompt,
    updateAvailable,
    updateApp,
    dismissUpdate
  } = useServiceWorker();
  const [showConnectionNotif, setShowConnectionNotif] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);
  let t0;
  let t1;
  if ($[0] !== isOnline || $[1] !== wasOffline) {
    t0 = () => {
      if (!isOnline && !wasOffline) {
        setWasOffline(true);
        setShowConnectionNotif(true);
        const timer = setTimeout(() => {
          setShowConnectionNotif(false);
        }, 5000);
        return () => clearTimeout(timer);
      } else {
        if (isOnline && wasOffline) {
          setWasOffline(false);
          setShowConnectionNotif(true);
          const timer_0 = setTimeout(() => {
            setShowConnectionNotif(false);
          }, 5000);
          return () => clearTimeout(timer_0);
        }
      }
    };
    t1 = [isOnline, wasOffline];
    $[0] = isOnline;
    $[1] = wasOffline;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  if ($[4] !== installPWA) {
    t2 = async () => {
      await installPWA();
    };
    $[4] = installPWA;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleInstall = t2;
  let t3;
  if ($[6] !== dismissUpdate || $[7] !== updateApp || $[8] !== updateAvailable) {
    t3 = <UpdateNotification isVisible={updateAvailable} onUpdate={updateApp} onDismiss={dismissUpdate} />;
    $[6] = dismissUpdate;
    $[7] = updateApp;
    $[8] = updateAvailable;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== isOnline || $[11] !== showConnectionNotif) {
    t4 = showConnectionNotif && <motion.div initial={{
      x: 400,
      opacity: 0
    }} animate={{
      x: 0,
      opacity: 1
    }} exit={{
      x: 400,
      opacity: 0
    }} transition={{
      type: "spring",
      stiffness: 300,
      damping: 30
    }} className="fixed top-20 right-4 z-50 max-w-sm"><div className={`rounded-xl shadow-2xl p-4 border-2 ${isOnline ? "bg-gradient-to-r from-green-50 to-emerald-50 border-green-500" : "bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-500"}`}><div className="flex items-center gap-3"><div className={`w-12 h-12 rounded-full flex items-center justify-center ${isOnline ? "bg-green-500" : "bg-yellow-500"}`}>{isOnline ? <Wifi className="w-6 h-6 text-white" /> : <WifiOff className="w-6 h-6 text-white" />}</div><div className="flex-1"><h4 className={`font-bold text-sm ${isOnline ? "text-green-900" : "text-yellow-900"}`}>{isOnline ? "Mode en ligne" : "Mode hors ligne"}</h4><p className={`text-xs ${isOnline ? "text-green-700" : "text-yellow-700"}`}>{isOnline ? "Connexion r\xE9tablie" : "Vous pouvez continuer \xE0 travailler"}</p></div><motion.div initial={{
            scaleX: 1
          }} animate={{
            scaleX: 0
          }} transition={{
            duration: 5,
            ease: "linear"
          }} className={`absolute bottom-0 left-0 h-1 origin-left rounded-bl-xl ${isOnline ? "bg-green-500" : "bg-yellow-500"}`} style={{
            width: "100%"
          }} /></div></div></motion.div>;
    $[10] = isOnline;
    $[11] = showConnectionNotif;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t4) {
    t5 = <AnimatePresence>{t4}</AnimatePresence>;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== isOnline) {
    t6 = !isOnline && <motion.div initial={{
      y: -100,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: -100,
      opacity: 0
    }} className="fixed top-0 left-0 right-0 z-40 bg-yellow-500 text-white px-4 py-2 shadow-lg"><div className="max-w-7xl mx-auto flex items-center justify-center gap-2"><WifiOff className="w-4 h-4" /><span className="text-sm font-medium">Mode hors ligne activé</span></div></motion.div>;
    $[15] = isOnline;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t6) {
    t7 = <AnimatePresence>{t6}</AnimatePresence>;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== dismissInstallPrompt || $[20] !== handleInstall || $[21] !== isInstallable) {
    t8 = isInstallable && <motion.div initial={{
      y: 100,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: 100,
      opacity: 0
    }} className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50"><div className="bg-white rounded-xl shadow-2xl p-4 border border-gray-200 relative"><button onClick={dismissInstallPrompt} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"><X className="w-5 h-5" /></button><div className="flex items-start gap-3 pr-6"><div className="w-10 h-10 bg-[#6059f7] rounded-lg flex items-center justify-center flex-shrink-0"><Download className="w-5 h-5 text-white" /></div><div className="flex-1"><h3 className="font-semibold text-gray-900 mb-1">Installer Learn2Link</h3><p className="text-sm text-gray-600 mb-3">Installez l'application pour un accès rapide et hors ligne</p><div className="flex gap-0"><button onClick={handleInstall} className="px-6 py-2 bg-[#6059f7] text-white rounded-l-lg text-sm font-semibold hover:bg-[#6059f7]/90 transition-colors">Installer</button><button onClick={dismissInstallPrompt} className="px-6 py-2 bg-gray-100 text-gray-600 rounded-r-lg text-sm font-semibold hover:bg-gray-200 transition-colors border-l border-gray-300">Plus tard</button></div></div></div></div></motion.div>;
    $[19] = dismissInstallPrompt;
    $[20] = handleInstall;
    $[21] = isInstallable;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t8) {
    t9 = <AnimatePresence>{t8}</AnimatePresence>;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t3 || $[26] !== t5 || $[27] !== t7 || $[28] !== t9) {
    t10 = <>{t3}{t5}{t7}{t9}</>;
    $[25] = t3;
    $[26] = t5;
    $[27] = t7;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10;
}